import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Sparkles, ArrowLeft, Send, MessageCircle, 
  Users, Calculator, Settings, LogOut, Loader2
} from 'lucide-react';
import { format, parseISO, isToday, isYesterday } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatMessageTime = (dateStr) => {
  const date = parseISO(dateStr);
  if (isToday(date)) {
    return format(date, 'h:mm a');
  } else if (isYesterday(date)) {
    return 'Yesterday ' + format(date, 'h:mm a');
  }
  return format(date, 'MMM d, h:mm a');
};

// Conversation List Component
const ConversationList = ({ conversations, selectedId, onSelect, loading }) => {
  if (loading) {
    return (
      <div className="p-4 space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
            <div className="w-12 h-12 rounded-full bg-[#F3E5AB]/50" />
            <div className="flex-1">
              <div className="h-4 bg-[#F3E5AB]/50 rounded w-24 mb-2" />
              <div className="h-3 bg-[#F3E5AB]/30 rounded w-32" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="p-8 text-center">
        <MessageCircle className="w-12 h-12 text-[#D4AF37]/30 mx-auto mb-4" />
        <p className="text-[#8C857B] text-sm">No conversations yet</p>
        <Link to="/matches" className="text-[#D4AF37] text-sm mt-2 inline-block hover:underline">
          Find matches to start chatting
        </Link>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-180px)]">
      <div className="p-2">
        {conversations.map((conv) => (
          <button
            key={conv.partner.id}
            onClick={() => onSelect(conv.partner.id)}
            className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left ${
              selectedId === conv.partner.id 
                ? 'bg-[#D4AF37]/10 border border-[#D4AF37]/30' 
                : 'hover:bg-[#F9F6F0]'
            }`}
            data-testid={`conversation-${conv.partner.id}`}
          >
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] flex items-center justify-center text-white font-heading text-lg flex-shrink-0">
              {conv.partner.full_name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-[#2D2A26] truncate">
                  {conv.partner.full_name?.split(' ')[0]}
                </h4>
                {conv.unread_count > 0 && (
                  <span className="w-5 h-5 rounded-full bg-[#D4AF37] text-white text-xs flex items-center justify-center">
                    {conv.unread_count}
                  </span>
                )}
              </div>
              {conv.last_message && (
                <p className="text-[#8C857B] text-sm truncate">
                  {conv.last_message.content}
                </p>
              )}
            </div>
          </button>
        ))}
      </div>
    </ScrollArea>
  );
};

// Chat Component
const ChatView = ({ partnerId, partnerName, token, userId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchMessages = async () => {
      if (!partnerId) return;
      setLoading(true);
      try {
        const response = await axios.get(`${API}/messages/${partnerId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessages(response.data);
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [partnerId, token]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    
    setSending(true);
    try {
      const response = await axios.post(`${API}/messages`, {
        receiver_id: partnerId,
        content: newMessage.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  if (!partnerId) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <MessageCircle className="w-16 h-16 text-[#D4AF37]/20 mx-auto mb-4" />
          <p className="text-[#8C857B]">Select a conversation to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Chat Header */}
      <div className="p-4 border-b border-[#E8E2D2] flex items-center gap-3">
        <Link 
          to={`/matches/${partnerId}`}
          className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] flex items-center justify-center text-white font-heading hover:scale-105 transition-transform"
        >
          {partnerName?.charAt(0)}
        </Link>
        <div>
          <h3 className="font-heading text-lg text-[#2D2A26]">{partnerName}</h3>
          <Link to={`/matches/${partnerId}`} className="text-[#D4AF37] text-xs hover:underline">
            View Profile
          </Link>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <Sparkles className="w-12 h-12 text-[#D4AF37]/30 mx-auto mb-4" />
            <p className="text-[#8C857B]">Start your cosmic conversation</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => {
              const isMine = msg.sender_id === userId;
              return (
                <motion.div
                  key={msg.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[75%] ${isMine ? 'order-1' : 'order-2'}`}>
                    <div className={`px-4 py-3 rounded-2xl ${
                      isMine 
                        ? 'bg-gradient-to-r from-[#D4AF37] to-[#B8860B] text-white rounded-br-md' 
                        : 'bg-white border border-[#E8E2D2] text-[#2D2A26] rounded-bl-md'
                    }`}>
                      <p className="text-sm leading-relaxed">{msg.content}</p>
                    </div>
                    <p className={`text-xs text-[#8C857B] mt-1 ${isMine ? 'text-right' : 'text-left'}`}>
                      {formatMessageTime(msg.created_at)}
                    </p>
                  </div>
                </motion.div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Message Input */}
      <form onSubmit={handleSend} className="p-4 border-t border-[#E8E2D2]">
        <div className="flex gap-3">
          <Input
            ref={inputRef}
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 bg-white/50 border-[#E8E2D2] focus:border-[#D4AF37] rounded-xl h-12"
            data-testid="message-input"
          />
          <Button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="rounded-xl btn-gold px-6 h-12"
            data-testid="send-message-btn"
          >
            {sending ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export const MessagesPage = () => {
  const { user, token, logout } = useAuth();
  const { partnerId } = useParams();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPartner, setSelectedPartner] = useState(null);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const response = await axios.get(`${API}/messages/conversations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setConversations(response.data);
        
        // If partnerId is provided, find the partner
        if (partnerId) {
          const conv = response.data.find(c => c.partner.id === partnerId);
          if (conv) {
            setSelectedPartner(conv.partner);
          } else {
            // Fetch partner info if not in conversations yet
            try {
              const matchRes = await axios.get(`${API}/matches/${partnerId}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              setSelectedPartner(matchRes.data.user);
            } catch (e) {
              console.error('Partner not found');
            }
          }
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConversations();
    
    // Poll for new conversations
    const interval = setInterval(fetchConversations, 10000);
    return () => clearInterval(interval);
  }, [token, partnerId]);

  const handleSelectConversation = (id) => {
    navigate(`/messages/${id}`);
    const conv = conversations.find(c => c.partner.id === id);
    if (conv) {
      setSelectedPartner(conv.partner);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white/60 backdrop-blur-xl border-b border-[#E8E2D2] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="messages-logo">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F3E5AB] to-[#D4AF37] flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-2xl text-[#2D2A26]">Intuitive Matchmaker</span>
            </Link>
            
            <nav className="flex items-center gap-6">
              <Link 
                to="/dashboard" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex"
                data-testid="nav-dashboard"
              >
                Dashboard
              </Link>
              <Link 
                to="/matches" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex items-center gap-2"
                data-testid="nav-matches"
              >
                <Users className="w-4 h-4" />
                Matches
              </Link>
              <Link 
                to="/calculator" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors font-medium hidden md:inline-flex items-center gap-2"
                data-testid="nav-calculator"
              >
                <Calculator className="w-4 h-4" />
              </Link>
              <Link 
                to="/settings" 
                className="text-[#4A453E] hover:text-[#D4AF37] transition-colors"
                data-testid="nav-settings"
              >
                <Settings className="w-5 h-5" />
              </Link>
              <button 
                onClick={logout}
                className="text-[#8C857B] hover:text-[#D4AF37] transition-colors"
                data-testid="logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </nav>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-6">
        <div className="mb-6">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[#8C857B] hover:text-[#D4AF37] transition-colors"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Dashboard</span>
          </Link>
        </div>

        <div className="ethereal-card rounded-3xl overflow-hidden" style={{ height: 'calc(100vh - 200px)' }}>
          <div className="grid md:grid-cols-[320px_1fr] h-full">
            {/* Conversations Sidebar */}
            <div className="border-r border-[#E8E2D2] bg-white/30">
              <div className="p-4 border-b border-[#E8E2D2]">
                <h2 className="font-heading text-xl text-[#2D2A26]">Messages</h2>
              </div>
              <ConversationList 
                conversations={conversations}
                selectedId={partnerId}
                onSelect={handleSelectConversation}
                loading={loading}
              />
            </div>

            {/* Chat Area */}
            <div className="bg-[#FDFBF7]/50">
              <ChatView 
                partnerId={partnerId}
                partnerName={selectedPartner?.full_name?.split(' ')[0]}
                token={token}
                userId={user?.id}
              />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MessagesPage;
